import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight, Download, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useResults } from '@/state/useResults';
import { useSession } from '@/state/session';
import { useMachineSparklines } from '@/state/useChartDays';
import { fmtNumber } from '@/domain/numbers';
import { formatRange } from '@/domain/time';
import { ACTIVITY_LABELS, MACHINE_TYPE_LABELS, PROVENANCE_LABELS } from '@/lib/labels';
import { machineLabel } from '@/lib/present';
import { exportMachinesCsv } from '@/lib/exports';
import { EmptyState, PageSkeleton, Panel, PanelHeader } from '@/components/app/States';
import { CoverageBadge, MachineRef, Note } from '@/components/app/Badges';
import { PageHeader } from '@/components/app/PageHeader';
import { KpiStrip, StripCell } from '@/components/app/Kpi';
import { ShareBar, Sparkline, CHART } from '@/components/app/Charts';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MaskinparkPanel } from '@/components/app/Maskinpark';
import { listMachines } from '@/lib/driftApi';
import { cn } from '@/lib/utils';

const COLS = [
  { key: 'machineId', label: 'Maskin', num: false },
  { key: 'activity', label: 'Aktivitet', num: false, hide: 'xl' },
  { key: 'engineHours', label: 'Motortimer (h)', num: true, hide: 'md' },
  { key: 'idleHours', label: 'Tomgang (h)', num: true },
  { key: 'idleShare', label: 'Tomgangsandel', num: true },
  { key: 'fuelLitres', label: 'Diesel (L)', num: true },
  { key: 'co2Kg', label: 'CO₂ (kg)', num: true, hide: 'md' },
  { key: 'allocationFraction', label: 'Prod.andel', num: true, hide: 'xl' },
  { key: 'trend', label: 'Trend diesel', num: false, hide: '2xl', sortable: false },
  { key: 'quality', label: 'Kvalitet', num: false, hide: 'sm' },
];

const HIDE = { sm: 'hidden sm:table-cell', md: 'hidden md:table-cell', lg: 'hidden lg:table-cell', xl: 'hidden xl:table-cell', '2xl': 'hidden 2xl:table-cell' };

function qualityLabel(m) {
  if (m.quality === 'complete') return { label: 'Komplett', status: 'complete' };
  if (m.quality === 'partial') return { label: `Delvis · ${m.missingCount} felt`, status: 'partial' };
  if (m.quality === 'quarantined') return { label: 'Karantene', status: 'missing' };
  return { label: 'Ingen data', status: 'not_applicable' };
}

export default function MaskinerPage() {
  const r = useResults();
  const { role } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('fane') === 'maskinpark' ? 'maskinpark' : 'drift';
  const setTab = (v) => {
    const next = new URLSearchParams(params);
    if (v === 'maskinpark') next.set('fane', 'maskinpark');
    else next.delete('fane');
    setParams(next, { replace: true });
  };
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'fuelLitres', dir: 'desc' });
  const sparks = useMachineSparklines(r.dataset, r.query);
  // «Trenger oppfølging» skal være synlig også fra drift-fanen: liten teller på fanen + varsel når maskiner ikke kan brukes.
  const [attention, setAttention] = useState(null);
  useEffect(() => {
    let on = true;
    listMachines()
      .then((d) => on && setAttention({ count: d.machines.filter((m) => m.needsFollowUp).length, blocked: d.summary?.blocked ?? 0, awaiting: d.summary?.awaitingClearance ?? 0 }))
      .catch(() => {});
    return () => {
      on = false;
    };
  }, [tab]);

  const curResult = r.current;
  const ready = r.ready;
  const rows = useMemo(() => {
    if (!ready || !curResult) return [];
    const term = q.trim().toLowerCase();
    let list = curResult.machines.filter((m) => !term || machineLabel(m.machineId).toLowerCase().includes(term) || m.machineId.toLowerCase().includes(term) || m.name.toLowerCase().includes(term) || (MACHINE_TYPE_LABELS[m.type] ?? '').toLowerCase().includes(term));
    list = [...list].sort((a, b) => {
      if (a.inScope !== b.inScope) return a.inScope ? -1 : 1;
      const av = sort.key === 'machineId' ? machineLabel(a.machineId) : a[sort.key];
      const bv = sort.key === 'machineId' ? machineLabel(b.machineId) : b[sort.key];
      if (typeof av === 'number' || typeof bv === 'number') return ((bv ?? -Infinity) - (av ?? -Infinity)) * (sort.dir === 'desc' ? 1 : -1);
      return String(av ?? '').localeCompare(String(bv ?? ''), 'nb') * (sort.dir === 'desc' ? -1 : 1);
    });
    return list;
  }, [ready, curResult, q, sort]);

  if (!r.ready) return <PageSkeleton />;
  const c = r.current;
  const inScope = c.machines.filter((m) => m.inScope);
  const outOfScope = c.machines.filter((m) => !m.inScope);
  const maxFuel = Math.max(1, ...c.machines.map((m) => m.fuelLitres));
  const hotCount = inScope.filter((m) => m.idleShare !== null && m.idleShare > 25).length;
  const partialCount = inScope.filter((m) => m.quality === 'partial').length;
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }));
  const machinesById = new Map((r.dataset?.machines ?? []).map((m) => [m.machineId, m]));

  return (
    <div className="space-y-6">
      <PageHeader title="Maskiner" lead={tab === 'maskinpark' ? 'Driftsstatus, feilmeldinger, service og vedlikehold. Samme maskiner som i produksjonsdataene.' : `${formatRange(r.fromDate, r.toDate)} · ${c.scopeLabel}`}>
        {tab === 'drift' && (
          <Button variant="outline" size="sm" className="h-9 rounded-[10px] bg-white" onClick={() => exportMachinesCsv(r, role)} data-testid="export-machines-csv-button">
            <Download className="mr-1.5 h-4 w-4" aria-hidden="true" /> CSV
          </Button>
        )}
      </PageHeader>

      {/* To faner: telematikk/drift i valgt periode (eksisterende) og maskinpark/service (register). */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-10 rounded-[10px] bg-[var(--hr-surface-2)] p-1" data-testid="machines-tabs">
          <TabsTrigger value="drift" className="h-8 rounded-[8px] px-4 text-[14px] data-[state=active]:bg-white data-[state=active]:shadow-[var(--hr-shadow-xs)]" data-testid="machines-tab-drift">
            Drift i perioden
          </TabsTrigger>
          <TabsTrigger value="maskinpark" className="h-8 rounded-[8px] px-4 text-[14px] data-[state=active]:bg-white data-[state=active]:shadow-[var(--hr-shadow-xs)]" data-testid="machines-tab-maskinpark">
            Maskinpark og service
            {attention?.count ? (
              <span className={cn('hr-tabular ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[12px] font-semibold', attention.blocked ? 'bg-[var(--hr-red-bg)] text-[var(--hr-red)]' : 'bg-[var(--hr-amber-bg)] text-[var(--hr-amber)]')} title={`${attention.count} maskiner trenger oppfølging`} data-testid="machines-tab-attention-count">
                {attention.count}
              </span>
            ) : null}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'drift' && attention && (attention.blocked > 0 || attention.awaiting > 0) && (
        <Note tone={attention.blocked ? 'red' : 'amber'} data-testid="machines-attention-note">
          {attention.blocked ? `${attention.blocked} ${attention.blocked === 1 ? 'maskin kan ikke brukes' : 'maskiner kan ikke brukes'}` : ''}
          {attention.blocked && attention.awaiting ? ' · ' : ''}
          {attention.awaiting ? `${attention.awaiting} ${attention.awaiting === 1 ? 'maskin avventer frigivelse' : 'maskiner avventer frigivelse'}` : ''}{' '}
          <button type="button" onClick={() => setTab('maskinpark')} className="font-medium underline underline-offset-2 hover:text-[var(--hr-heading)]" data-testid="machines-attention-link">
            Se hva som trenger oppfølging
          </button>
        </Note>
      )}

      {tab === 'maskinpark' ? (
        <MaskinparkPanel />
      ) : (
        <>

      {c.scope === 'all' || r.scope === 'all' ? <Note tone="amber">Omfang «alle maskiner»: forholdstall inkluderer utlasting og service og er ikke sammenlignbare med produksjonsområdet.</Note> : null}
      {c.quarantined.length > 0 && <Note tone="amber">{c.quarantined.length} intervall(er) er satt i karantene (ugyldige verdier) og inngår ikke i summene.</Note>}

      <KpiStrip data-testid="machines-summary-strip">
        <StripCell label="Maskiner i beregningsområdet" value={fmtNumber(inScope.length, 0)} meta={`${outOfScope.length} utenfor (kontekst)`} testId="machines-summary-count" />
        <StripCell label="Motortimer" value={fmtNumber(c.engineHours.value, 1)} unit="h" meta={`${fmtNumber(c.idleHours.value, 1)} h tomgang · ${c.idleShare.value === null ? '–' : `${fmtNumber(c.idleShare.value, 1)} %`}`} testId="machines-summary-engine" />
        <StripCell label="Diesel" value={fmtNumber(c.fuelLitres.value, 0)} unit="L" meta={c.fuelLitres.status === 'partial' ? 'Registrert, delvis' : 'Komplett grunnlag'} metaTone={c.fuelLitres.status === 'partial' ? 'amber' : undefined} testId="machines-summary-fuel" />
        <StripCell label="Å følge opp" value={fmtNumber(hotCount, 0)} meta={`${hotCount === 1 ? 'maskin' : 'maskiner'} over 25 % tomgang${partialCount ? ` · ${partialCount} med delvis grunnlag` : ''}`} metaTone={hotCount ? 'amber' : undefined} testId="machines-summary-hot" />
      </KpiStrip>

      <Panel data-testid="machines-panel">
        <PanelHeader
          title={`${rows.length} maskiner`}
          actions={
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--hr-muted)]" aria-hidden="true" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk maskin …" aria-label="Søk i maskiner" className="h-9 w-[220px] rounded-[10px] bg-white pl-9 text-[14px]" data-testid="machines-search-input" />
            </div>
          }
        />
        <div className="hr-scroll overflow-x-auto">
          <Table data-testid="machines-table" className="[&_td]:py-3 [&_th]:h-10">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {COLS.map((col, i) => {
                  const active = sort.key === col.key;
                  const Icon = !active ? ArrowUpDown : sort.dir === 'desc' ? ArrowDown : ArrowUp;
                  return (
                    <TableHead key={col.key} className={cn(col.num && 'text-right', col.hide && HIDE[col.hide], i === 0 && 'pl-5')} aria-sort={active ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}>
                      {col.sortable === false ? (
                        <span>{col.label}</span>
                      ) : (
                        <button type="button" onClick={() => toggleSort(col.key)} className={cn('inline-flex items-center gap-1 rounded-sm transition-colors duration-150 hover:text-[var(--hr-heading)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]', active && 'text-[var(--hr-heading)]')} data-testid={`machines-sort-${col.key}`}>
                          {col.label}
                          <Icon className={cn('h-3 w-3', !active && 'opacity-50')} aria-hidden="true" />
                        </button>
                      )}
                    </TableHead>
                  );
                })}
                <TableHead className="w-12 pr-5"><span className="sr-only">Åpne</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((m, idx) => {
                const ql = qualityLabel(m);
                const to = r.linkTo(`/maskiner/${m.machineId}`);
                const showNums = m.inScope || r.scope === 'all';
                const hot = m.idleShare !== null && m.idleShare > 25;
                const sp = sparks.byMachine[m.machineId];
                const firstOut = !m.inScope && (idx === 0 || rows[idx - 1].inScope);
                return (
                  <React.Fragment key={m.machineId}>
                    {firstOut && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={COLS.length + 1} className="bg-[var(--hr-surface-2)] py-2 pl-5 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">Utenfor beregningsområdet · kontekst</TableCell>
                      </TableRow>
                    )}
                    <TableRow
                      className={cn('cursor-pointer', !m.inScope && 'text-[var(--hr-muted)]')}
                      data-testid={`machines-table-row-${m.machineId}`}
                      onClick={(e) => {
                        if (e.target.closest('a')) return;
                        navigate(to);
                      }}
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && navigate(to)}
                    >
                      <TableCell className="pl-5">
                        <MachineRef machineId={m.machineId} name={machinesById.get(m.machineId)?.fictionalName ?? m.name} to={to} testId={`machines-link-${m.machineId}`} />
                        <div className="mt-0.5 text-[13px] text-[var(--hr-muted)]">{MACHINE_TYPE_LABELS[m.type]}</div>
                      </TableCell>
                      <TableCell className={HIDE.xl}>{ACTIVITY_LABELS[m.activity]}</TableCell>
                      <TableCell className={cn('hr-num text-right', HIDE.md)}>{showNums ? fmtNumber(m.engineHours, 2) : '–'}</TableCell>
                      <TableCell className="hr-num text-right">{showNums ? fmtNumber(m.idleHours, 2) : '–'}</TableCell>
                      <TableCell className="text-right">
                        {m.idleShare === null ? (
                          <span className="text-[var(--hr-muted)]">–</span>
                        ) : (
                          <span className="inline-flex items-center justify-end gap-2.5">
                            <ShareBar value={m.idleShare} max={50} tone={hot ? 'amber' : 'navy'} width={56} label={`Tomgangsandel ${fmtNumber(m.idleShare, 1)} %`} className="hidden md:inline-flex" />
                            <span className={cn('hr-num font-medium', hot ? 'text-[var(--hr-amber)]' : '')}>
                              {fmtNumber(m.idleShare, 1)} %{hot && <span className="sr-only"> – over 25 %</span>}
                            </span>
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex items-center justify-end gap-2.5">
                          {showNums && <ShareBar value={m.fuelLitres} max={maxFuel} width={56} label={`Diesel ${fmtNumber(m.fuelLitres, 0)} L`} className="hidden md:inline-flex" />}
                          <span className="hr-num text-[16px] font-semibold text-[var(--hr-heading)]">{showNums ? fmtNumber(m.fuelLitres, 0) : '–'}</span>
                        </span>
                      </TableCell>
                      <TableCell className={cn('hr-num text-right', HIDE.md)} data-testid={`machines-co2-${m.machineId}`}>
                        {showNums && m.co2Kg !== null ? (
                          <>
                            {fmtNumber(m.co2Kg, 0)}
                            {m.co2Source !== 'reported' && <span className="ml-1 text-[12px] text-[var(--hr-muted)]">{PROVENANCE_LABELS[m.co2Source] ?? m.co2Source}</span>}
                          </>
                        ) : (
                          <span className="text-[var(--hr-muted)]">–</span>
                        )}
                      </TableCell>
                      <TableCell className={cn('hr-num text-right', HIDE.xl)}>
                        {m.inScope ? `${fmtNumber(m.allocationFraction * 100, 0)} %` : '0 %'}
                        {m.allocationMethod === 'demo_assumption' && <span className="ml-1 text-[13px] text-[var(--hr-amber)]">anslag</span>}
                      </TableCell>
                      <TableCell className={HIDE['2xl']}>{sp && showNums ? <Sparkline values={sp.fuel} color={CHART.bar} width={104} height={28} label={`Diesel per dag, ${machineLabel(m.machineId)}`} /> : <span className="text-[13px] text-[var(--hr-muted)]">–</span>}</TableCell>
                      <TableCell className={HIDE.sm}>
                        <CoverageBadge status={ql.status} label={ql.label} />
                      </TableCell>
                      <TableCell className="pr-5 text-right">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--hr-muted-2)]" aria-hidden="true">
                          <ChevronRight className="h-4 w-4" />
                        </span>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {rows.length === 0 && (
          <div className="p-4">
            <EmptyState type="no_results" title="Ingen maskiner matcher søket" description={`Søket «${q}» ga ingen treff.`} onReset={() => setQ('')} resetLabel="Tøm søk" />
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 border-t border-[var(--hr-hairline)] px-5 py-3 text-[13px] text-[var(--hr-muted)]">
          <span className="hr-tabular">
            Sum: {fmtNumber(c.engineHours.value, 1)} h motortimer · {fmtNumber(c.idleHours.value, 1)} h tomgang · {fmtNumber(c.fuelLitres.value, 0)} L diesel{c.fuelLitres.status === 'partial' ? ' (registrert, delvis)' : ''}
          </span>
          <span>Trend siste {sparks.days.length} dager</span>
        </div>
      </Panel>
        </>
      )}
    </div>
  );
}
